import Cache from '@fs/zion-cache'
import type { PersonId } from '@fs/zion-tree-types'
import { mutate } from 'swr'
import { fetcher, useSwrRequest } from './useSwrRequest'

export type PortraitStatus = 'approving' | 'failed' | 'processing' | 'ready'

// Mirror the Tree Portrait Service's PortraitProcessingStateModel and PortraitScreeningStateModel enums
export type ProcessingStateModel = 'FAILED' | 'PROCESSED' | 'PROCESSING' | 'UNPROCESSED'
export type ScreeningStateModel = 'APPROVED' | 'RESTRICTED' | 'SCREENING' | 'UNSCREENED'

// The portrait service's PortraitModel carries more (urls, crop, artifact id); these are all we read
export type PortraitStates = {
  processingStateModel: ProcessingStateModel
  screeningStateModel: ScreeningStateModel
}

const cache = new Cache({ storeName: 'portrait_r9', encrypted: process.env.NODE_ENV === 'production' })

const swrKey = (pid?: PersonId): string | null => (pid ? `/service/memories/tps/persons/${pid}/portrait` : null)

function toPortraitStatus(portrait?: PortraitStates): PortraitStatus | undefined {
  if (!portrait) return undefined
  if (portrait.processingStateModel === 'FAILED') return 'failed'
  if (portrait.processingStateModel !== 'PROCESSED') return 'processing'
  if (portrait.screeningStateModel !== 'APPROVED') return 'approving'
  return 'ready'
}

/**
 * Every failure resolves to no status instead of rejecting: a person with no portrait answers 404,
 * and a state we couldn't read is nothing to show a badge for. Swallowing it here also keeps a 5xx
 * from the memories service out of useSwrRequest's error overlay, which would otherwise put a
 * page-level error in front of the person over a missing badge.
 */
async function readPortraitStatus(url: string, zionCache?: Cache): Promise<PortraitStatus | undefined> {
  try {
    return toPortraitStatus(await fetcher<PortraitStates>(url, zionCache))
  } catch {
    return undefined
  }
}

/**
 * Reads how far a person's portrait has gotten through processing and screening.
 *
 * This is deliberately its own request rather than part of the focus person: awaiting it alongside
 * the person card meant a slow, hanging, or throttled memories service held the whole person page
 * in its loading state (up to the 30s request timeout, times retry-axios' retries on 429s and
 * 5xxs) while the person data itself was already in hand.
 */
export default function usePortraitStatus(pid?: PersonId): PortraitStatus | undefined {
  const { data } = useSwrRequest<PortraitStatus, PortraitStates>(swrKey(pid), cache, {
    emulator: readPortraitStatus,
  })
  return data
}

export function mutatePortraitStatus(pid: PersonId): Promise<unknown> {
  return mutate(swrKey(pid))
}
